import { computed, defineAsyncComponent, defineComponent, ref } from 'vue';
import { AppGeneralVariables } from 'src/plugins/general/variables';
import { ChatMiscDataPlugin } from 'src/features/messaging/chat/components/methods/misc';
import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';
import emoji from './components/emoji.vue';
import fileCheckInput from 'src/features/upload/components/input.vue';
import listingVideo from './components/video.vue';
export default defineComponent({
    name: 'chat-input',
    components: {
        emoji,
        fileCheckInput,
        listingVideo,
        shareListing: defineAsyncComponent(() => import(
        /* webpackChunkName: "Listing" */
        './components/shareListing.vue')),
        stackedIcon: defineAsyncComponent(() => import(
        /* webpackChunkName: "Stacked Icon" */
        'components/icon/stackedIcon.vue'))
    },
    props: {
        hideAttach: Boolean,
        uploadable: Boolean,
        listing: {
            type: Object
        },
        listingLoading: Boolean,
        type: String,
        btnColor: {
            type: String,
            default: 'primary'
        },
        dark: Boolean,
        preventBlank: Boolean,
        videoCount: {
            type: Number,
            default: 0
        },
        otherListingCount: {
            type: Number,
            default: 0
        }
    },
    emits: [
        'custom-order',
        'input',
        'send-message',
        'update-input-height',
        'upload-file',
        'focus',
        'blur'
    ],
    setup(props, context) {
        const { styleType } = AppGeneralVariables();
        const $q = useQuasar();
        const shareClass = computed(() => {
            return {
                'cursor-pointer text-center q-pa-sm': true,
                'col-4': styleType.value === 'desktop',
                'chat-share-col': styleType.value !== 'desktop'
            };
        });
        const target = ref(null);
        /*****
         * Chat data
         ****/
        const { appType, isChatUser, receiver } = ChatMiscDataPlugin();
        /********
         * Send
         *********/
        /**
         * Send message and reset height
         */
        function send() {
            if (props.preventBlank) {
                if (!text.value) {
                    return;
                }
            }
            const replacedLineBreaks = text.value.replace(/(\r\n|\n|\r)/gm, '<br />');
            context.emit('send-message', replacedLineBreaks);
            // clear data
            text.value = '';
            showing.value = false;
            typing.value = false;
            // reset height
            if (target.value) {
                target.value.style.height = "auto";
                target.value.style.height = `36px`;
            }
            context.emit('update-input-height', 36);
        }
        /**
         * When user press enter, send message
         * @param e keyboard event to detect enter
         */
        function checkSend(e) {
            if ((!$q.platform.is.mobile) &&
                (e.keyCode === 13 && !e.shiftKey)) {
                e.preventDefault();
                send();
            }
        }
        function preventer(e) {
            e.preventDefault();
        }
        /**********
         * Check typing
          *********/
        const typing = ref(false);
        /**
         * Check if user is typing
         * @value typed value
         */
        function checkInput(value) {
            if (value) {
                if (typing.value)
                    return;
                typing.value = true;
                context.emit('input', true);
                return;
            }
            typing.value = false;
            context.emit('input', false);
        }
        /**
         * Input text
         */
        const text = ref('');
        /**
         * Update height of the input
         * so that the chat scroll area height offset can
         * be updated
         */
        function updateHeight() {
            if (!target.value || (target.value && target.value.scrollHeight >= 142))
                return;
            target.value.style.height = "auto";
            target.value.style.height = `${target.value.scrollHeight}px`;
            context.emit('update-input-height', target.value.scrollHeight);
        }
        /**
         * update text value on input
         * @value text value
         */
        function setVal(value) {
            text.value = value;
            checkInput(value);
            updateHeight();
        }
        /**********
         * More functions
         *********/
        const showUpload = ref(false);
        function showMore() {
            showUpload.value = !showUpload.value;
        }
        function closeUploadMenu() {
            showUpload.value = false;
        }
        /**********
         * Upload
         *********/
        const imgFormRef = ref(null);
        const fileFormRef = ref(null);
        function clearFileInput() {
            imgFormRef.value?.reset();
            fileFormRef.value?.reset();
        }
        function emitUploadFile(file) {
            context.emit('upload-file', file, 'file');
        }
        function emitUploadImg(file) {
            context.emit('upload-file', file, 'img');
        }
        /**********
         * Listing
         *********/
        const showListingShare = ref(false);
        /**********
         * Video
         *********/
        const videoRef = ref(null);
        // select video
        function showVideoListClick() {
            videoRef.value?.showVideoListClick();
        }
        /**********
         * Emoji
         *********/
        const showing = ref(false);
        function showEmoji() {
            showing.value = !showing.value;
        }
        /**
         * Add emoji to text
         * @value emoji value
         */
        function parseEmoji(value) {
            text.value += value;
        }
        /**
         * Close all menus
         */
        function closeAllMenus() {
            showUpload.value = false;
            showing.value = false;
        }
        /**********
         * Focus
         *********/
        function setTarget(e) {
            if (!target.value && (
            // @ts-expect-error ignore
            e.target.nodeName === 'TEXTAREA')) {
                target.value = e.target;
            }
            // hide all menus when input is focused
            closeAllMenus();
        }
        function blur() {
            context.emit('blur');
            checkInput('');
        }
        function focus(e) {
            context.emit('focus');
            setTarget(e);
        }
        return {
            styleType,
            shareClass,
            appType,
            isChatUser,
            receiver,
            target,
            checkSend,
            preventer,
            send,
            typing,
            checkInput,
            text,
            setVal,
            imgFormRef,
            fileFormRef,
            showUpload,
            closeUploadMenu,
            showMore,
            setTarget,
            blur,
            focus,
            clearFileInput,
            emitUploadFile,
            emitUploadImg,
            showListingShare,
            videoRef,
            showVideoListClick,
            showing,
            parseEmoji,
            showEmoji,
            closeAllMenus
        };
    }
});
